import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api, { STATIC_BASE_URL } from '../api';
import { QRCodeSVG } from 'qrcode.react';
import QRCode from 'qrcode';
import { PDFDownloadLink } from '@react-pdf/renderer';
import InvoiceDocument from './InvoiceDocument';
import BackButton from '../components/BackButton';

export default function InvoiceViewPage() {
  const { invoiceId } = useParams();
  const [showShipTo, setShowShipTo] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  const [logo, setLogo] = useState(null);
  const [customer, setCustomer] = useState({});
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);

  const UPI_ID = 'h.patne5577@axl';

  useEffect(() => {
    async function fetchData() {
      const invRes = await api.get(`/invoices/${invoiceId}`);
      const inv = invRes.data.invoice;
      setInvoice(inv);
      setItems(invRes.data.items || []);
      setCustomer({
        name: inv.CustomerName || 'N/A',
        address: inv.Address || 'N/A',
        phone: inv.Phone || 'N/A',
        gstin: inv.GSTIN || 'N/A',
      });
      const setRes = await api.get('/settings/get');
      setSettings(setRes.data);
      const logoRes = await api.get('/logo/current');
      setLogo(logoRes.data.logo);
    }
    fetchData();
  }, [invoiceId]);

  useEffect(() => {
    async function generateQRCode() {
      if (!settings.BusinessName || !invoice) return;
      try {
        const qrValue = `upi://pay?pa=${UPI_ID}&pn=${settings.BusinessName}&am=${invoice.TotalAmount || 0}&cu=INR`;
        const qrDataUrl = await QRCode.toDataURL(qrValue);
        setQrCodeDataUrl(qrDataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    }
    generateQRCode();
  }, [settings, invoice]);

  const phoneNumbers = (settings.Phone || '')
    .split(',')
    .map(num => num.trim())
    .filter(Boolean);

  if (!invoice) return <div>Loading invoice...</div>;

  // Totals using DB values directly
  const totalCgst = items.reduce((sum, i) => sum + Number(i.CGST || 0), 0);
  const totalSgst = items.reduce((sum, i) => sum + Number(i.SGST || 0), 0);
  const subTotal = items.reduce((sum, i) => sum + Number(i.Amount || (i.Quantity * i.Rate)), 0);
  const netTotal = subTotal + totalCgst + totalSgst;
  const totalTax = totalCgst + totalSgst;

  // GST percent for summary table (show as CSV if mixed)
  const cgstPercArr = items.map(i => Number(i.GSTPercent || 0) / 2).filter(p => p > 0);
  const sgstPercArr = cgstPercArr; // CGST and SGST % always equal for local invoices
  const cgstPerc = cgstPercArr.length ? [...new Set(cgstPercArr)].join(', ') : '0';
  const sgstPerc = sgstPercArr.length ? [...new Set(sgstPercArr)].join(', ') : '0';

  // HSN for summary
  const hsnSet = Array.from(new Set(items.map(i => i.HSN).filter(Boolean)));

  // For table display
  const MIN_ROWS = 5;
  const rowCount = items.length;
  const emptyRows = rowCount < MIN_ROWS ? MIN_ROWS - rowCount : 0;

  const numberToWords = (num) => {
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
      'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    if ((num = num.toString()).length > 9) return 'Amount too large';
    const n = ('000000000' + num).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n) return '';
    let str = '';
    str += (n[1] !== '00') ? (a[Number(n[1])] || (b[n[1][0]] + ' ' + a[n[1][1]])) + ' Crore ' : '';
    str += (n[2] !== '00') ? (a[Number(n[2])] || (b[n[2][0]] + ' ' + a[n[2][1]])) + ' Lakh ' : '';
    str += (n[3] !== '00') ? (a[Number(n[3])] || (b[n[3][0]] + ' ' + a[n[3][1]])) + ' Thousand ' : '';
    str += (n[4] !== '0')  ? (a[Number(n[4])] || (b[n[4][0]] + ' ' + a[n[4][1]])) + ' Hundred ' : '';
    str += (n[5] !== '00') ? ((str ? 'and ' : '') + (a[Number(n[5])] || (b[n[5][0]] + ' ' + a[n[5][1]]))) + ' ' : '';
    return str + 'Rupees';
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-GB');
  const dueDate = new Date(invoice.InvoiceDate);
  dueDate.setDate(dueDate.getDate() + 30);

  return (
    <div style={{ maxWidth: '123mm', margin: 'auto' }}>
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <BackButton />
        <div>
          <label style={{ fontSize: 13, marginRight: 8, userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={showShipTo}
              style={{ marginRight: 4 }}
              onChange={e => setShowShipTo(e.target.checked)}
            />
            Show Ship To
          </label>
          <PDFDownloadLink
            document={
              <InvoiceDocument
                invoice={invoice}
                items={items}
                settings={settings}
                logo={logo}
                customer={customer}
                qrCodeDataUrl={qrCodeDataUrl}
                showShipTo={showShipTo}
              />
            }
            fileName={`${customer.name}_${invoice.InvoiceNo}.pdf`}
            style={{
              marginLeft: 0,
              background: '#007bff',
              color: '#fff',
              border: 'none',
              padding: '5px 10px',
              borderRadius: 4
            }}
          >
            {({ loading }) => (loading ? 'Preparing PDF...' : 'Download PDF')}
          </PDFDownloadLink>
        </div>
      </div>

      <div className="invoice-container" style={{ width: '123mm', minHeight: '148mm', padding: '7mm', border: '1px solid #000', background: '#fff' }}>
        <style>{`
          @media print { .no-print { display: none !important; } }
          #invoiceContent, #invoiceContent * { font-size: 8px !important; line-height: 1 !important; }
        `}</style>
        <div id="invoiceContent">
           
          {/* Header */}
<div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 8 }}>
  {logo && (
    <img
      src={`${STATIC_BASE_URL}/uploads/logos/${logo.FileName}`}
      alt="Logo"
      style={{ height: '40px', marginRight: 10, flexShrink: 0 }}
    />
  )}
  <div style={{ flex: 1 }}>
    <div style={{ fontWeight: 'bold', fontSize: '11px' }}>{settings.BusinessName}</div>
    <div style={{ fontSize: '9px' }}>{settings.Address}</div>
    <div style={{ fontSize: '9px' }}>
      GSTIN: {settings.GSTIN} {settings.PANNumber ? `| PAN: ${settings.PANNumber}` : ''}
    </div>
  </div>
  {/* Phones, only one "Ph:" label, numbers as lines */}
  <div style={{ textAlign: 'right', minWidth: 90 }}>
    <span style={{ fontSize: '9px', fontWeight: 'bold' }}>Ph:</span>
    {(settings.Phone || '')
      .split(',')
      .filter(x => x.trim())
      .map((num, idx) => (
        <div key={idx} style={{ fontSize: '9px', whiteSpace: 'nowrap', display: 'block' }}>
          {num.trim()}
        </div>
      ))}
  </div>
</div>



          <div style={{ textAlign: 'center', marginBottom: 8, fontSize: '9px', textDecoration: 'underline' }}>TAX INVOICE</div>

          {/* Bill/Ship/Invoice Details */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
            <tbody>
              <tr>
                <td style={{ width: showShipTo ? '33%' : '50%', border: '1px solid #000', padding: 4, verticalAlign: 'top' }}>
                  <strong>BILL TO</strong><br />
                  {customer.name}<br />
                  {customer.address}<br />
                  Mobile: {customer.phone}<br />
                  GSTIN: {customer.gstin}
                </td>
                {showShipTo && (
                  <td style={{ width: '33%', border: '1px solid #000', padding: 4, verticalAlign: 'top' }}>
                    <strong>SHIP TO</strong><br />
                    {customer.name}<br />
                    {customer.address}<br />
                    Mobile: {customer.phone}<br />
                    GSTIN: {customer.gstin}
                  </td>
                )}
                <td style={{ width: showShipTo ? '34%' : '50%', border: '1px solid #000', padding: 4, verticalAlign: 'top' }}>
                  Invoice No: {invoice.InvoiceNo}<br />
                  Invoice Date: {formatDate(invoice.InvoiceDate)}<br />
                  Due Date: {formatDate(dueDate)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Items Table */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, textAlign: 'center' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #000', padding: 4 }}>No.</th>
                <th style={{ border: '1px solid #000', padding: 4, textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>HSN/SAC</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>Size</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>Qty</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>Rate</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #000', padding: 4 }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #000', padding: 4, textAlign: 'left' }}>{item.Description}</td>
                  <td style={{ border: '1px solid #000', padding: 4 }}>{item.HSN || '-'}</td>
                  <td style={{ border: '1px solid #000', padding: 4 }}>{item.Size}</td>
                  <td style={{ border: '1px solid #000', padding: 4 }}>{item.Quantity}</td>
                  <td style={{ border: '1px solid #000', padding: 4 }}>{parseFloat(item.Rate).toFixed(2)}</td>
                  <td style={{ border: '1px solid #000', padding: 4 }}>{Number(item.Amount || (item.Quantity * item.Rate)).toFixed(2)}</td>
                </tr>
              ))}
              {/* Blank rows for minimum height */}
              {Array(emptyRows).fill(0).map((_, i) => (
                <tr key={`blank-${i}`}>
                  {[...Array(7)].map((_, tdIdx) => (
                    <td
                      key={tdIdx}
                      style={{
                        borderLeft: '1px solid #000',
                        borderRight: '1px solid #000',
                        borderTop: 'none',
                        borderBottom: 'none',
                        padding: 15,
                        height: 24
                      }}
                    >
                      &nbsp;
                    </td>
                  ))}
                </tr>
              ))}
              {/* CGST Row */}
              <tr>
                <td style={{ border: '1px solid #000', borderTop: '1px solid #000', borderBottom: 'none', background: '#fff' }}></td>
                <td
                  style={{
                    borderLeft: '1px solid #000',
                    borderTop: '1px solid #000',
                    borderBottom: 'none',
                    borderRight: '1px solid #000',
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                    textAlign: 'left',
                    background: '#fff'
                  }}
                  colSpan={5}
                >
                  CGST @{cgstPerc}%
                </td>
                <td
                  style={{
                    border: '1px solid #000',
                    borderTop: '1px solid #000',
                    borderBottom: 'none',
                    fontWeight: 600,
                    background: '#fff'
                  }}
                >
                  ₹ {totalCgst.toFixed(2)}
                </td>
              </tr>
              {/* SGST Row */}
              <tr>
                <td style={{ border: '1px solid #000', borderTop: 'none', borderBottom: 'none', background: '#fff' }}></td>
                <td
                  style={{
                    borderLeft: '1px solid #000',
                    borderTop: 'none',
                    borderBottom: 'none',
                    borderRight: '1px solid #000',
                    fontStyle: 'italic',
                    fontWeight: 'bold',
                    textAlign: 'left',
                    background: '#fff'
                  }}
                  colSpan={5}
                >
                  SGST @{sgstPerc}%
                </td>
                <td
                  style={{
                    border: '1px solid #000',
                    borderTop: 'none',
                    borderBottom: 'none',
                    fontWeight: 600,
                    background: '#fff'
                  }}
                >
                  ₹ {totalSgst.toFixed(2)}
                </td>
              </tr>
              {/* TOTAL Row */}
              <tr>
                <td colSpan={5} style={{ border: '1px solid #000', background: '#feedd4', fontWeight: 'bold' }}></td>
                <td style={{ border: '1px solid #000', background: '#feedd4', fontWeight: 'bold' }}>TOTAL</td>
                <td style={{ border: '1px solid #000', background: '#feedd4', fontWeight: 'bold' }}>
                  {netTotal.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Total Amount in Words */}
          <div style={{ border: '1px solid #000', padding: 6, marginBottom: 0, fontSize: '8px' }}>
            <strong>Total Amount (in words): </strong>{numberToWords(netTotal.toFixed(0))}
          </div>

          {/* Tax Summary Table (Statutory) */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8, textAlign: 'center' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #000', padding: 4 }}>HSN/SAC</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>Taxable Value</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>CGST %</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>CGST Amt</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>SGST %</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>SGST Amt</th>
                <th style={{ border: '1px solid #000', padding: 4 }}>Total Tax</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #000', padding: 4 }}>
                  {hsnSet.length ? hsnSet.join(', ') : '-'}
                </td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{subTotal.toFixed(2)}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{cgstPerc}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{totalCgst.toFixed(2)}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{sgstPerc}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{totalSgst.toFixed(2)}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{totalTax.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ border: '1px solid #000', padding: 4 }}><strong>Total</strong></td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{subTotal.toFixed(2)}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}></td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{totalCgst.toFixed(2)}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}></td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{totalSgst.toFixed(2)}</td>
                <td style={{ border: '1px solid #000', padding: 4 }}>{totalTax.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          {/* Footer Section with Bank Details, QR Code, Terms & Signature */}
          <div style={{ display: 'flex', border: '1px solid #000', fontSize: '8px', textAlign: 'center' }}>
            <div style={{ flex: 1, borderRight: '1px solid #000', padding: 6 }}>
              <h6>Bank Details</h6>
              <div>{settings.BusinessName}</div>
              <div>IFSC: {settings.IFSCCode}</div>
              <div>A/C No: {settings.AccountNumber}</div>
              <div>Branch: {settings.BankBranch}</div>
            </div>
            <div style={{ flex: 1, borderRight: '1px solid #000', padding: 6 }}>
              <h6>Payment QR Code</h6>
              <QRCodeSVG
                value={`upi://pay?pa=${UPI_ID}&pn=${settings.BusinessName}&am=${netTotal.toFixed(2)}&cu=INR`}
                size={90}
              />
              <div>UPI ID: {UPI_ID}</div>
            </div>
            <div style={{ flex: 1, borderRight: '1px solid #000', padding: 6 }}>
              <h6>Terms & Conditions</h6>
              <div>1. Goods once sold will not be taken back or exchanged.</div>
              <div>2. All disputes subject to jurisdiction only.</div>
            </div>
            <div style={{ flex: 1, padding: 6 }}>
              <h6>Authorized Signatory</h6>
              <div style={{ borderTop: '1px solid #000', marginTop: 30 }}></div>
              <div>For {settings.BusinessName}</div>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: '9px' }}>
            <u>TAX INVOICE DUPLICATE</u>
          </div>
        </div>
      </div>
    </div>
  );
}
